import React from 'react';
import { motion } from 'framer-motion';
import { FiShield, FiUsers, FiAward, FiCheck, FiArrowRight } from 'react-icons/fi';
import * as FaIcons from 'react-icons/fa';
import SafeIcon from '../common/SafeIcon';
import { Link } from 'react-router-dom';

const { FaStar } = FaIcons;

const values = [
  {
    icon: FiShield,
    title: 'Uncompromising Quality',
    desc: 'We use only industrial-grade materials — the same systems used in aircraft hangars and pharmaceutical plants.'
  },
  {
    icon: FiUsers,
    title: 'Crew You Can Trust',
    desc: "Our installers are W2 employees, not subcontractors. They're trained, background-checked, and take pride in their craft."
  },
  {
    icon: FiAward,
    title: 'Warranty Backed',
    desc: 'Every installation comes with our industry-leading warranty. If it fails, we fix it. No questions asked.'
  },
];

const About = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <div className="relative h-80 md:h-[500px] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://media-manager-c.questera.ai/greta-media/b23737eec191c3f5b765303f85ce3a4932597ae054cc2b3dadfd019ed91c748ca1e6808982e16c775001fce05a7fb0b4/images/aW1hZ2UvcG5n/5c980b02fae98bba170d82c1e5aa61c5.png" 
            alt="About All Star Epoxy" 
            className="w-full h-full object-cover scale-105"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-red-900/80 via-black/50 to-black/80" />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20 w-full">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <span className="text-red-400 text-sm font-black tracking-[0.2em] uppercase mb-4 block">Our Story</span>
            <h1 className="text-5xl md:text-7xl lg:text-8xl font-black text-white tracking-tight">About Us</h1>
          </motion.div>
        </div>
      </div>

      {/* Intro Section */}
      <section className="py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-24 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <span className="text-red-600 text-sm font-black tracking-[0.2em] uppercase mb-4 block">Who We Are</span>
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-gray-900 mb-8 leading-tight tracking-tight">
                Setting the Standard in<br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-600 to-blue-600">Epoxy Flooring.</span>
              </h2>
              <p className="text-gray-600 text-xl mb-6 leading-relaxed font-light">
                All Star Epoxy was founded with one mission: install the highest quality epoxy floors in the industry, period. 
                We grew from a two-man crew in a single truck to a fully equipped team completing projects across the region — 
                without ever compromising on quality.
              </p>
              <p className="text-gray-600 text-xl mb-12 leading-relaxed font-light">
                We specialize strictly in residential garages and commercial warehouses. By narrowing our focus, we've become 
                world-class at exactly these two environments.
              </p>
              
              <div className="grid grid-cols-2 gap-8 border-t border-gray-200 pt-12">
                {[
                  { value: '15+', label: 'Years in Business' },
                  { value: '2,400+', label: 'Projects Completed' },
                  { value: '5M+', label: 'Sq Ft Installed' },
                  { value: '100%', label: 'Satisfaction Rate' },
                ].map((s, i) => (
                  <motion.div 
                    key={i}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                  >
                    <div className="text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-red-600 to-blue-600 mb-2">{s.value}</div>
                    <div className="text-gray-500 font-bold uppercase tracking-wider text-sm">{s.label}</div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="relative"
            >
              <div className="aspect-[4/5] rounded-[2.5rem] overflow-hidden shadow-2xl border border-gray-100">
                <img 
                  src="https://media-manager-c.questera.ai/greta-media/b23737eec191c3f5b765303f85ce3a4932597ae054cc2b3dadfd019ed91c748ca1e6808982e16c775001fce05a7fb0b4/images/aW1hZ2UvanBlZw==/15ce0561618377893538a047841f7c0a.jpeg" 
                  alt="Our Crew at Work" 
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
              </div>
              
              <div className="absolute -bottom-8 -right-8 bg-red-600 text-white p-10 rounded-[2rem] shadow-xl hidden md:block max-w-xs border border-red-500">
                <div className="flex gap-1.5 mb-4">
                  {[...Array(5)].map((_, i) => <SafeIcon key={i} icon={FaStar} className="text-yellow-400 text-lg" />)}
                </div>
                <p className="font-bold text-lg leading-snug">"Best crew we've ever worked with. Flawless execution and results."</p>
                <p className="text-red-200 text-sm font-bold uppercase tracking-wider mt-4">— Warehouse Client</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-32 bg-gray-50 border-y border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-20"
          >
            <span className="text-blue-600 text-sm font-black tracking-[0.2em] uppercase mb-4 block">Our Principles</span>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-gray-900">Why Clients Choose Us</h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {values.map((v, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.15 }}
                className="bg-white border border-gray-100 hover:border-red-200 rounded-[2rem] p-12 transition-all duration-300 group shadow-md hover:shadow-xl"
              >
                <div className="w-20 h-20 bg-red-50 group-hover:bg-red-600 rounded-2xl flex items-center justify-center mb-8 transition-colors border border-red-100">
                  <SafeIcon icon={v.icon} className="text-red-600 group-hover:text-white text-3xl transition-colors" />
                </div>
                <h3 className="text-2xl font-black text-gray-900 mb-4">{v.title}</h3>
                <p className="text-gray-600 text-lg leading-relaxed font-light">{v.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* The Guarantee */}
      <section className="py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-20 items-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="rounded-[2.5rem] overflow-hidden aspect-[4/3] shadow-2xl border border-gray-100"
            >
              <img 
                src="https://media-manager-c.questera.ai/greta-media/b23737eec191c3f5b765303f85ce3a4932597ae054cc2b3dadfd019ed91c748ca1e6808982e16c775001fce05a7fb0b4/images/aW1hZ2UvanBlZw==/b57b6fc9c4fda80eb5277417408644ea.jpg" 
                alt="Premium Epoxy Result" 
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-red-600 text-sm font-black tracking-[0.2em] uppercase mb-4 block">Every Job Includes</span>
              <h2 className="text-4xl lg:text-5xl font-black text-gray-900 mb-10 tracking-tight">Our Promise to You</h2>
              
              <ul className="space-y-6 mb-12">
                {[
                  'Free on-site consultation and estimate',
                  'Full diamond grinding surface preparation',
                  'Crack and joint repair before coating',
                  'Multi-layer coating application system',
                  'UV-stable, polyaspartic topcoat',
                  'Post-install walkthrough and approval',
                  'Industry-leading written warranty',
                ].map((item, i) => (
                  <motion.li 
                    key={i}
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.08 }}
                    className="flex items-center gap-5 text-gray-700 text-lg font-light"
                  >
                    <div className="w-8 h-8 rounded-full bg-blue-50 border border-blue-100 flex items-center justify-center flex-shrink-0">
                      <SafeIcon icon={FiCheck} className="text-blue-600 text-sm" />
                    </div>
                    {item}
                  </motion.li>
                ))}
              </ul>

              <Link 
                to="/contact"
                className="inline-flex items-center gap-3 px-10 py-5 bg-red-600 hover:bg-blue-700 text-white font-black rounded-full transition-all group shadow-lg text-lg"
              >
                Get Your Free Quote
                <SafeIcon icon={FiArrowRight} className="group-hover:translate-x-1.5 transition-transform" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;